// マニュアル照会 (back: knowledge app)。社内マニュアル・行政通知を根拠に業務照会へ答える。

// 出典の種別。回答時の参照順序 (notice → interpretation → audit → company_rule) でもある。
// audit = 行政の指導監査で使われる資料。疑義解釈 (事務連絡) ではないので別の段に置く。
export type KnowledgeSourceKind =
  | 'notice'
  | 'interpretation'
  | 'audit'
  | 'company_rule'

// 回答 1 文の根拠。資料が差し替わっても当時の表示が復元できるよう、back が書き込み時に固定する。
//
// **画面が読むのは describeCitation が並べる 5 つだけ。** document_id と source_kind は
// 描かないが消さない — 前者は資料名が改定で変わっても発言から台帳を辿れる唯一の鍵、
// 後者は表示文字列 (source_kind_label) と違って改称しない機械値で、記録を種別で数えるときの
// 軸になる。表示に使われていないことを理由に外すと、保存済みの発言から追跡手段が消える。
export interface KnowledgeCitation {
  n: number
  document_id: string
  document_title: string
  source_kind: KnowledgeSourceKind
  source_kind_label: string
  heading_path: string
  page: number | null
  // 施行日 (ISO)。改定前後のタイミング判断に要るため出典に添える。未設定なら null。
  effective_date: string | null
}

export interface KnowledgeMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations: KnowledgeCitation[]
  created_at: string
}

export interface KnowledgeConversation {
  id: string
  title: string
  created_at: string
  updated_at: string
}

export interface KnowledgeConversationDetail extends KnowledgeConversation {
  messages: KnowledgeMessage[]
}

// 1 往復 (質問と回答)。回答だけでは画面が新規会話の初期表示を組み立てられず、
// 遷移直後に空状態が一瞬出るため、back は質問側の発言も返す。
export interface KnowledgeExchange {
  messages: KnowledgeMessage[]
}

export interface KnowledgeAskResult extends KnowledgeExchange {
  conversation_id: string
}

// 生成に失敗したときの応答。会話と質問は残っているので、新規会話では
// conversation_id が返る (返らないと利用者からは質問が消えたのに履歴にだけ残る)。
export interface KnowledgeAskError {
  error: string
  conversation_id?: string
}

// 出典 1 行の表記。種別 / 資料名 / 見出し / ページ / 施行日 を中黒で連ねる。
// 走査して読む監査情報のため、自然文にせずスロットを並べる。
//
// 同じ書式が back の `answer.source_label` にもある (yakureki-back knowledge/services/answer.py)。
// あちらはモデルに読ませる資料の見出しで、用途が違うので 1 つにまとめない。ただしスロットの
// 並びがずれると「モデルが根拠と呼んだもの」と「利用者が読む出典」の説明が食い違うため、
// 片方を変えるときはもう片方も見ること。
export const describeCitation = (citation: KnowledgeCitation): string =>
  [
    citation.source_kind_label,
    citation.document_title,
    citation.heading_path,
    citation.page === null ? '' : `p.${citation.page}`,
    citation.effective_date === null ? '' : `${citation.effective_date} 施行`,
  ]
    .filter(Boolean)
    .join(' / ')
